import React, { ChangeEvent, useEffect, useState } from "react";
import {
  AppBar,
  Box,
  Dialog,
  IconButton,
  Link,
  Slide,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";
import { makeStyles } from "@mui/styles";
import AppTextbox from "../AppTextbox";
import DeleteIcon from "../../assets/images/delete-icon.svg";
import AddIcon from "../../assets/images/add-icon-secondary.svg";
import EditIcon from "../../assets/images/edit-icon.svg";
import TickIcon from "../../assets/images/tick-icon-green.svg";
import CancelIcon from "../../assets/images/cancel-icon-grey.svg";
import RestoreIcon from "../../assets/images/restore.png";

const useStyles = makeStyles((theme) => ({
  input: {
    "& textarea": {},
  },
  deleted: {
    "& fieldset": {
      border: `0.3px solid ${theme.palette.error.main} !important`,
    },
  },
  addButton: {
    border: `0.3px solid ${theme.palette.primary.main} !important`,
  },
  strike: {
    position: "relative",
    "& div.before": {
      content: " ",
      position: "absolute",
      top: "50%",
      left: 0,
      borderBottom: "1px solid #111",
      width: "100%",
    },
  },
}));

export type ReplaceItem = {
  description: string;
  oldContent: string;
  newContent: string;
  id?: string;
  isDeleted?: boolean;
  workflow_sequence_id?: string;
};
const DEFAULT_ROW: ReplaceItem = {
  description: "",
  oldContent: "",
  newContent: "",
};

const ReplaceFieldsContainer: React.FC<{
  item: ReplaceItem;
  index: number;
  viewType: string;
  updateItem: (item: ReplaceItem, index: number) => void;
  removeItem: (index: number) => void;
}> = ({ item, viewType, updateItem, index, removeItem }) => {
  const classes = useStyles();
  const [description, setDescription] = useState(item.description);
  const [oldContent, setOldContent] = useState(item.oldContent);
  const [newContent, setNewContent] = useState(item.newContent);
  const [isEditable, setEditable] = useState(item.id ? false : true);
  const isValid = () =>
    description.length !== 0 &&
    oldContent.length !== 0 &&
    newContent.length !== 0;

  const handleSubmit = () => {
    updateItem({ ...item, oldContent, newContent, description }, index);
    setEditable(false);
  };

  const handleRemove = () => {
    removeItem(index);
    setEditable(false);
  };

  useEffect(() => {
    setDescription(item.description);
    setOldContent(item.oldContent);
    setNewContent(item.newContent);
  }, [item]);

  return (
    <Box
      display="flex"
      alignItems="center"
      marginBottom={2}
      gap={2}
      m={1}
      justifyContent={"center"}
    >
      <AppTextbox
        placeholder="Description"
        value={description}
        onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
          setDescription(e.target.value);
        }}
        classes={{
          root: `${classes.input} ${item.isDeleted ? classes.deleted : ""}`,
        }}
        multiline={true}
        rows={1}
        disabled={isEditable === false}
      />
      <AppTextbox
        placeholder="Old Content"
        value={oldContent}
        onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
          setOldContent(e.target.value);
        }}
        classes={{
          root: `${classes.input} ${item.isDeleted ? classes.deleted : ""}`,
        }}
        multiline={true}
        rows={1}
        disabled={isEditable === false}
      />
      <AppTextbox
        placeholder="New Content"
        value={newContent}
        onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
          setNewContent(e.target.value);
        }}
        classes={{
          root: `${classes.input} ${item.isDeleted ? classes.deleted : ""}`,
        }}
        multiline={true}
        rows={1}
        disabled={isEditable === false}
      />
      {viewType === "CREATE_FLOW_VIEW" && (
        <>
          {isEditable === false && (
            <Box ml={"auto"}>
              <Tooltip title={"Edit"}>
                <span>
                  <IconButton
                    sx={{ padding: 0.5, opacity: item.isDeleted ? 0.5 : 1 }}
                    onClick={() => {
                      setEditable(true);
                    }}
                    data-testid="edit-replacement-item"
                    disabled={item.isDeleted}
                  >
                    <img src={EditIcon} alt="close" height="24" width="24" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={item.isDeleted ? "Restore" : "Delete"}>
                <IconButton
                  sx={{ padding: 0.5 }}
                  onClick={handleRemove}
                  data-testid="remove-added-replacement"
                >
                  <img
                    src={item.isDeleted ? RestoreIcon : DeleteIcon}
                    alt="close"
                    height="24"
                    width="24"
                  />
                </IconButton>
              </Tooltip>
            </Box>
          )}
          {isEditable && (
            <Box ml={"auto"}>
              <Tooltip title={"Update"}>
                <span>
                  <IconButton
                    sx={{ padding: 0.5, opacity: isValid() ? 1 : 0.5 }}
                    onClick={handleSubmit}
                    data-testid="update-replacement-item"
                    disabled={isValid() === false}
                  >
                    <img src={TickIcon} alt="update" height="24" width="24" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={"Cancel"}>
                <span>
                  <IconButton
                    sx={{ padding: 0.5 }}
                    onClick={() => {
                      setEditable(false);
                      setDescription(item.description);
                      setOldContent(item.oldContent);
                      setNewContent(item.newContent);
                    }}
                    data-testid="cancel-update-replacement"
                  >
                    <img src={CancelIcon} alt="close" height="24" width="24" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const PreviewWithModification: React.FC<{
  modifications: ReplaceItem[];
  domPath: string;
}> = ({ modifications, domPath }) => {
  const [modifiedHtmlContent, setModifiedHtmlContent] = useState("");
  const [htmlContent, setHtmlContent] = useState("");
  const [open, setOpen] = React.useState(false);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };
  const modifyDomContent = (
    htmlText: string,
    domModifications: ReplaceItem[] = []
  ) => {
    domModifications.map((modification: ReplaceItem) => {
      if (!modification.isDeleted) {
        htmlText = htmlText
          .split(modification.oldContent)
          .join(modification.newContent);
      }
    });
    return htmlText;
  };
  useEffect(() => {
    const fetchDom = async () => {
      const domPathResponse = await fetch(domPath, { method: "GET" });
      const htmlText = await domPathResponse.text();
      setHtmlContent(htmlText);
    };
    fetchDom();
  }, [domPath]);

  useEffect(() => {
    const modifiedHtmlText = modifyDomContent(htmlContent, modifications);
    setModifiedHtmlContent(modifiedHtmlText);
  }, [htmlContent, modifications]);

  return (
    <React.Fragment>
      <Typography variant="h5" style={{ textAlign: "left", margin: "8px" }}>
        <Link
          onClick={handleClickOpen}
          role="button"
          style={{ cursor: "pointer" }}
        >
          Click here
        </Link>
        {" to view the modified page"}
      </Typography>
      <Dialog
        fullScreen
        open={open}
        onClose={handleClose}
        TransitionComponent={Transition}
      >
        <AppBar sx={{ position: "relative" }}>
          <Toolbar>
            <Typography
              sx={{ ml: "auto", flex: 1 }}
              variant="h4"
              component="div"
            >
              Preview with modifications
            </Typography>
            <IconButton
              edge="start"
              color="inherit"
              onClick={handleClose}
              aria-label="close"
            >
              <img src={CancelIcon} alt="close" height="24" width="24" />
            </IconButton>
          </Toolbar>
        </AppBar>
        <div>
          <div dangerouslySetInnerHTML={{ __html: modifiedHtmlContent }} />
        </div>
      </Dialog>
    </React.Fragment>
  );
};

const ReplaceFormContainer: React.FC<{
  domPath: string;
  items: ReplaceItem[];
  viewType: string;
  handleUpdateDomModification: (item: ReplaceItem, index: number) => void;
  handleRemoveDomModification: (index: number) => void;
}> = ({
  domPath,
  items,
  viewType,
  handleUpdateDomModification,
  handleRemoveDomModification,
}) => {
  const classes = useStyles();
  const [replaceItems, setReplaceItems] = useState<ReplaceItem[]>(items);
  const [enableAddButton, setEnableAddButton] = useState(false);

  const handleAddRow = () => {
    setReplaceItems([...replaceItems, DEFAULT_ROW]);
  };

  const updateItem = (item: ReplaceItem, index: number) => {
    handleUpdateDomModification(item, index);
  };

  const removeItem = (index: number) => {
    handleRemoveDomModification(index);
  };

  useEffect(() => {
    setReplaceItems(items);
  }, [items]);

  return (
    <div>
      <PreviewWithModification domPath={domPath} modifications={items} />
      {replaceItems.map((item, index) => (
        <ReplaceFieldsContainer
          item={item}
          key={index}
          updateItem={updateItem}
          removeItem={removeItem}
          index={index}
          viewType={viewType}
        />
      ))}
      {viewType === "CREATE_FLOW_VIEW" && (
        <Box
          display="flex"
          alignItems="center"
          justifyContent={"center"}
          marginBottom={2}
          gap={2}
          mx={1}
        >
          <Tooltip title={"Add new replacement"}>
            <span>
              <IconButton
                sx={{ padding: 0.5 }}
                onClick={handleAddRow}
                data-testid="add-another-replacement"
                classes={{ root: classes.addButton }}
              >
                <img src={AddIcon} alt="close" height="24" width="24" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      )}
    </div>
  );
};

export default ReplaceFormContainer;
